import { Spinner } from "react-bootstrap";
import { FaChartLine, FaChevronDown, FaChevronUp, FaFileExport, FaUtensils } from "react-icons/fa6";
import { FaEdit, FaTimes, FaSave } from "react-icons/fa";
import { formatSalesValue } from "../../../utils/formatUtils";
import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const CentralSnapshotTable = ({
    reportData,
    expandedReports,
    reportDetails,
    groupItemsByType,
    toggleReportExpansion,
    formattedDate,
    loadingDetails,
    handleCostingNavigation,
    handleExport,
    loading,
    token,
    fetchReportData,
    setExpandedReports,
    setReportDetails,
    getReportKey
}) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [editingReportId, setEditingReportId] = useState(null);
    const [editedItems, setEditedItems] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [editingType, setEditingType] = useState(null);

    const handleEditClick = (reportId, type) => {
        setEditingReportId(reportId);
        setEditingType(type);

        const reportItems =
            reportDetails[reportId]?.fnb_costing_reports
                ?.find(r => r.report_for === type)
                ?.items_actual_costing || [];

        const initialEdits = {};

        reportItems.forEach(item => {
            initialEdits[item.id] = item.physical_count;
        });

        setEditedItems(initialEdits);
    };

    const handleCancelEdit = () => {
        setEditingReportId(null);
        setEditingType(null);
        setEditedItems({});
    };

    const handlePhysicalCountChange = (itemId, value) => {
        setEditedItems(prev => ({
            ...prev,
            [itemId]: value
        }));
    };

    const handleSaveChanges = async () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save these changes?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                setIsSaving(true);
                                const updates = Object.entries(editedItems).map(([id, physical_count]) => ({
                                    snapshot_detail_id: parseInt(id),
                                    physical_count: parseFloat(physical_count) || 0
                                }));

                                await axios.post(
                                    `${baseUrl}/update-physical-counts-${editingType.toLowerCase()}`,
                                    {
                                        token,
                                        updates
                                    }
                                );

                                toast.success("Physical counts updated successfully");
                                setExpandedReports([]);
                                setReportDetails([]);
                                fetchReportData();
                            } catch (error) {
                                toast.error("Failed to update physical counts");
                                console.error("Update error:", error);
                            } finally {
                                setIsSaving(false);
                                setEditingReportId(null);
                                setEditingType(null);
                                setEditedItems({});
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'update-physical-stocks'
            }
        );
    };

    const formatNumber = (num) => {
        const parsed = parseFloat(num);
        return parsed.toString();
    }

    return (
        <div className="rounded-md border overflow-x-auto max-h-[700px] tiny-scrollbar">
            <table className="min-w-full overflow-y-auto divide-y divide-gray-200 text-sm text-center">
                <thead className="bg-gray-100 uppercase">
                    {/* TOP HEADER */}
                    <tr>
                        <th className="p-2 text-xs font-medium text-gray-700">Created At</th>
                        <th className="p-2 text-xs font-medium text-gray-700">Start Date</th>
                        <th className="p-2 text-xs font-medium text-gray-700">End Date</th>
                        <th className="p-2 text-xs font-medium border-r text-gray-700">Total</th>
                        <th className="p-2 text-xs font-medium border-r text-gray-700">Missing</th>
                        <th className="p-2 text-xs font-medium text-gray-700">Accuracy</th>
                        <th className="p-2 text-xs font-medium text-gray-700">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                    {reportData.snapshot_reports?.length > 0 ? (
                        reportData.snapshot_reports.map((report) => {
                            const reportKey = getReportKey(report);
                            const isExpanded = expandedReports.includes(reportKey);
                            const details = reportDetails[reportKey];
                            const items =
                                details?.fnb_costing_reports?.flatMap(report =>
                                    (report.items_actual_costing || []).map(item => ({
                                        ...item,
                                        type: report.report_for
                                    }))
                                ) || [];

                            const groupedItems = groupItemsByType(items);

                            return (
                                <>
                                    <tr
                                        key={reportKey}
                                        className='hover:bg-gray-50 hover:cursor-pointer'
                                        onClick={() => toggleReportExpansion(report)}
                                    >
                                        <td className="p-3 whitespace-nowrap">
                                            {formattedDate(report.created_at)}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">
                                            {formattedDate(report.start_date)}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">
                                            {formattedDate(report.end_date)}
                                        </td>
                                        <td className="p-3 whitespace-nowrap border-r">
                                            {report.total_items || 0}
                                        </td>
                                        <td className="p-3 whitespace-nowrap border-r">
                                            {report.missing_items || 0}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">
                                            {report.accuracy || 0}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">
                                            <button className="text-gray-500 hover:text-gray-700">
                                                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                            </button>
                                        </td>
                                    </tr>

                                    {isExpanded && (
                                        <tr>
                                            <td colSpan={10} className="p-4 bg-gray-50 shadow-md">
                                                {loadingDetails[reportKey] ? (
                                                    <div className="text-center my-4">
                                                        <Spinner animation="border" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </Spinner>
                                                    </div>
                                                ) : details ? (
                                                    <>
                                                        <div className="flex gap-3 mb-3">
                                                            <button
                                                                onClick={() => handleCostingNavigation(report)}
                                                                className="flex items-center gap-1 px-3 py-2 rounded-sm text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md"
                                                            >
                                                                <FaChartLine className="h-3 w-3" />
                                                                <FaUtensils className="h-3 w-3" />
                                                                View Costing Analysis
                                                            </button>

                                                            <button
                                                                onClick={() => handleExport(report)}
                                                                className="flex items-center gap-1 px-3 py-2 rounded-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md"
                                                            >
                                                                <FaFileExport className="h-3 w-3" />
                                                                Export
                                                            </button>
                                                        </div>

                                                        <div className="space-y-5">
                                                            {Object.entries(groupedItems).map(([type, items]) => {
                                                                const totalClosingAmount = items.reduce((acc, item) => acc + Number(item.physical_cost || 0), 0);
                                                                const isEditing =
                                                                    editingReportId === reportKey &&
                                                                    editingType === type;

                                                                return (
                                                                    <div key={type} className="mb-8">
                                                                        <div className="flex justify-between mb-3 items-end">
                                                                            <h3 className="text-lg font-semibold mb-0">{type}</h3>

                                                                            {/* <div className="flex gap-3">
                                                                                {!isEditing ? (
                                                                                    <button
                                                                                        onClick={() => handleEditClick(reportKey, type)}
                                                                                        className="flex items-center gap-1 px-3 py-2 rounded-sm text-sm font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 hover:shadow-md"
                                                                                    >
                                                                                        <FaEdit className="h-3 w-3" />
                                                                                        Edit Physical Count
                                                                                    </button>
                                                                                ) : (
                                                                                    <>
                                                                                        <button
                                                                                            onClick={handleSaveChanges}
                                                                                            disabled={isSaving}
                                                                                            className="flex items-center gap-1 px-3 py-2 rounded-sm text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 hover:shadow-md disabled:opacity-50"
                                                                                        >
                                                                                            {isSaving ? (
                                                                                                <Spinner animation="border" size="sm" />
                                                                                            ) : (
                                                                                                <>
                                                                                                    <FaSave className="h-3 w-3" />
                                                                                                    Save Changes
                                                                                                </>
                                                                                            )}
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={handleCancelEdit}
                                                                                            className="flex items-center gap-1 px-3 py-2 rounded-sm text-sm font-medium bg-red-100 text-red-800 hover:bg-red-200 hover:shadow-md"
                                                                                        >
                                                                                            <FaTimes className="h-3 w-3" />
                                                                                            Cancel
                                                                                        </button>
                                                                                    </>
                                                                                )}
                                                                            </div> */}
                                                                        </div>

                                                                        <div className="rounded-md border overflow-x-auto">
                                                                            <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                                                                                <thead className="bg-gray-100">
                                                                                    <tr>
                                                                                        {[
                                                                                            "Group", "Item Name", "UOM", "Opening", "Received", "Wastage", "Consumption", "Closing", "Discrepancy", "Closing Amount"
                                                                                        ].map((col, idx) => (
                                                                                            <th
                                                                                                key={idx}
                                                                                                className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider"
                                                                                            >
                                                                                                {col}
                                                                                            </th>
                                                                                        ))}
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody className="bg-white divide-y divide-gray-100">
                                                                                    {items.map((item, index) => (
                                                                                        <tr key={index} className="hover:bg-gray-50">
                                                                                            <td className="px-3 py-2">{item.group_name}</td>
                                                                                            <td className="px-3 py-2">{item.item_name}</td>
                                                                                            {/* <td className="px-3 py-2">{formatSalesValue(item.rate)}</td> */}
                                                                                            <td className="px-3 py-2">{item.uom}</td>
                                                                                            <td className="px-3 py-2">{formatNumber(item.opening_count)}</td>
                                                                                            <td className="px-3 py-2">{formatNumber(item.total_received)}</td>
                                                                                            <td className="px-3 py-2">{formatNumber(item.wastage_count)}</td>
                                                                                            <td className="px-3 py-2">{formatNumber(item.total_consumed)}</td>
                                                                                            <td className="px-3 py-2">
                                                                                                {isEditing ? (
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        className="w-20 p-1 border rounded text-center"
                                                                                                        value={editedItems[item.id] || ''}
                                                                                                        onChange={(e) => handlePhysicalCountChange(item.id, e.target.value)}
                                                                                                        step="0.01"
                                                                                                    />
                                                                                                ) : (
                                                                                                    formatNumber(item.physical_count)
                                                                                                )}
                                                                                            </td>
                                                                                            <td className="px-3 py-2">{formatNumber(item.discrepancy_count)}</td>
                                                                                            <td className="px-3 py-2">{formatSalesValue(item.physical_cost)}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>

                                                                        <div className="w-full flex justify-end mt-2">
                                                                            <div className="p-3 bg-gray-200 font-medium text-gray-800 rounded-md">
                                                                                Total Closing Amount: Rs. {formatSalesValue(totalClosingAmount)}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center text-gray-500 py-4">
                                                        No details available
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={10} className="px-3 py-4 text-gray-500">
                                {loading ? "Loading..." : "No data available"}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    )
}

export default CentralSnapshotTable
